import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { fetchAll } from '@/services/report/fetchAll'
import { post } from '@/services/report/post'
import { arrayValues } from '@/utils/array-values'
import { formatBrDateToIso } from '@/utils/format-br-date-to-iso'
import { getLast31DaysPeriod } from '@/utils/get-last-31-days-period'

interface IReportProps {
  activeDrawer: boolean
  currentFilters: Filters | undefined
  handleFilter: (filtros: Filters) => Promise<void>
  handleLimitChange: (newLimit: number) => void
  handleSelectItem: (item: string[], action: boolean) => void
  handleShowReports: () => void
  isLoading: boolean
  limit: number
  report: any[]
  page: number
  results: number
  search: string
  selectItems: any[]
  setActiveDrawer: React.Dispatch<React.SetStateAction<boolean>>
  setCurrentFilters: React.Dispatch<React.SetStateAction<Filters | undefined>>
  setLimit: React.Dispatch<React.SetStateAction<number>>
  setReport: React.Dispatch<React.SetStateAction<any[]>>
  setPage: React.Dispatch<React.SetStateAction<number>>
  setSearch: React.Dispatch<React.SetStateAction<string>>
  setSelectItems: React.Dispatch<React.SetStateAction<any[]>>
  setTableData: React.Dispatch<React.SetStateAction<any[]>>
  tableData: any[]
}

type Filters = {
  dataDoItem: any
  operadora: any
  status: any
  grupoEconomico: any
  fornecedorLogistica: any
  colaboradores: any
  agrupamento?: any
}

const ReportContext = createContext<IReportProps | undefined>(undefined)

const ReportProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [tableData, setTableData] = useState<any[]>([])
  const [limit, setLimit] = useState(10)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [currentFilters, setCurrentFilters] = useState<Filters | undefined>(
    undefined
  )
  const [results, setResults] = useState<number>(0)
  const [activeDrawer, setActiveDrawer] = useState(false)
  const [selectItems, setSelectItems] = useState([])
  const [report, setReport] = useState([])

  const rastreioApiUrl = process.env.REACT_APP_API_RASTREIO_URL

  const dates = getLast31DaysPeriod().split(' - ')
  const defaultStartDate = formatBrDateToIso(dates[0])
  const defaultEndDate = formatBrDateToIso(dates[1])
  const period = `${defaultStartDate} - ${defaultEndDate}`

  const userString = localStorage.getItem('user')
  const user = userString ? JSON.parse(userString) : {}

  useEffect(() => {
    const defaultFilters = {
      dataDoItem: period,
      operadora: null,
      status: null,
      grupoEconomico: null,
      fornecedorLogistica: null,
      colaboradores: null,
      agrupamento: null
    }

    setCurrentFilters(defaultFilters)
  }, [])

  const fetchData = async () => {
    setIsLoading(true)
    const allData = await fetchAll({
      campo: 'dataDeCriacao',
      comprimento: limit,
      direcao: 'desc',
      idDoUsuarioSolicitante: user.userId,
      inicio: page * limit - limit,
      procurar: 'AcompanhamentoLogistica'
    })

    setTableData(allData.items)
    setResults(allData.total)
    setIsLoading(false)
  }

  const postData = async (filtros: Filters) => {
    setIsLoading(true)

    let startDate = ''
    let endDate = ''

    if (filtros.dataDoItem) {
      const dates = filtros.dataDoItem.split(' - ')
      startDate = new Date(dates[0]).toISOString().split('T')[0]
      endDate = new Date(dates[1]).toISOString().split('T')[0]
    }

    const operadoraValues = arrayValues(filtros.operadora ?? null)
    const statusValues = arrayValues(filtros.status ?? null)
    const fornecedorLogisticaValues = arrayValues(
      filtros.fornecedorLogistica ?? null
    )
    const grupoEconomicoValues = filtros.grupoEconomico?.value
      ? [filtros.grupoEconomico.value]
      : null

    const payload = {
      dataDaSolicitacao: new Date().toISOString().split('T')[0],
      dataDeCriacao: new Date().toISOString().split('T')[0],
      idDoUsuarioSolicitante: user.userId,
      tipo: 1,
      subTipo: 1,
      status: 0,
      etapa: 0,
      versao: 2,
      requisicaoBuscarDados: {
        nome: 'AcompanhamentoLogistica',
        url: `${rastreioApiUrl}/Relatorio/acompanhamento-logisticas`,
        metodo: 'POST',
        corpo: JSON.stringify({
          dataFinal: endDate,
          dataInicial: startDate,
          agrupamento: filtros.agrupamento ?? null,
          idsEmpregadores: grupoEconomicoValues,
          idsFornecedorLogistica:
            fornecedorLogisticaValues.length > 0
              ? fornecedorLogisticaValues
              : null,
          idsOperadoras: operadoraValues.length > 0 ? operadoraValues : null,
          situacoes: statusValues.length > 0 ? statusValues.map(Number) : null,
          termo: filtros.colaboradores ?? null
        })
      }
    }

    const allData = await post(payload)

    if (allData?.sucesso) {
      await fetchData()
    }

    setIsLoading(false)
  }

  const handleFilter = async (filtros: Filters) => {
    await postData(filtros)
  }

  const handleShowReports = async () => {
    await fetchData()
  }

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit)
    setPage(1)
  }

  useEffect(() => {
    fetchData()
  }, [page, limit])

  const handleSelectItem = (item: string[], action: boolean) => {
    setSelectItems(prevItems => {
      if (action === false) {
        return prevItems.filter(i => i !== item)
      }
      return action ? [...prevItems, item] : prevItems
    })
  }

  const contextValue = useMemo(
    () => ({
      activeDrawer,
      currentFilters,
      handleFilter,
      handleLimitChange,
      handleSelectItem,
      handleShowReports,
      isLoading,
      limit,
      report,
      page,
      results,
      search,
      selectItems,
      setActiveDrawer,
      setCurrentFilters,
      setLimit,
      setReport,
      setPage,
      setSearch,
      setSelectItems,
      setTableData,
      tableData
    }),
    [
      activeDrawer,
      currentFilters,
      handleFilter,
      handleLimitChange,
      handleSelectItem,
      handleShowReports,
      isLoading,
      limit,
      report,
      page,
      results,
      search,
      selectItems,
      setActiveDrawer,
      setCurrentFilters,
      setLimit,
      setReport,
      setPage,
      setSearch,
      setSelectItems,
      setTableData,
      tableData
    ]
  )
  return (
    <ReportContext.Provider value={contextValue}>
      {children}
    </ReportContext.Provider>
  )
}

const useReportContext = () => {
  const context = useContext(ReportContext)
  if (context === undefined) {
    throw new Error('useReportContext must be used within a ReportProvider')
  }
  return context
}

export { ReportProvider, useReportContext }
